import { Loader2, AlertTriangle, Inbox, KeySquare, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useStudioLocale } from '@/i18n/use-studio-locale'

interface CenteredProps {
  children: React.ReactNode
}

function Centered({ children }: CenteredProps) {
  return (
    <div className="flex h-full min-h-[200px] items-center justify-center p-6">
      <Card className="max-w-md p-6 text-center">{children}</Card>
    </div>
  )
}

export function VocabularyLoading() {
  const { strings } = useStudioLocale()
  const copy = strings.designSystem.vocabularyPanel
  return (
    <Centered>
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{copy.loading}</p>
      </div>
    </Centered>
  )
}

interface VocabularyErrorProps {
  error: Error
  onRetry: () => void
}

export function VocabularyError({ error, onRetry }: VocabularyErrorProps) {
  const { strings } = useStudioLocale()
  const copy = strings.designSystem.vocabularyPanel
  return (
    <Centered>
      <div className="flex flex-col items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-destructive" />
        <p className="text-sm font-medium">{copy.errorTitle}</p>
        <p className="text-xs text-muted-foreground">{error.message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {copy.retry}
        </Button>
      </div>
    </Centered>
  )
}

export function VocabularyEmpty() {
  const { strings } = useStudioLocale()
  const copy = strings.designSystem.vocabularyPanel
  return (
    <Centered>
      <div className="flex flex-col items-center gap-3">
        <Inbox className="h-5 w-5 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{copy.empty}</p>
      </div>
    </Centered>
  )
}

export function VocabularyUnavailable() {
  const { strings } = useStudioLocale()
  const copy = strings.designSystem.vocabularyPanel
  return (
    <Centered>
      <div className="flex flex-col items-center gap-3">
        <KeySquare className="h-5 w-5 text-muted-foreground" />
        <p className="text-sm font-medium">{copy.unavailableTitle}</p>
        <p className="text-xs text-muted-foreground">
          {copy.unavailableDetail}
        </p>
      </div>
    </Centered>
  )
}

interface VocabularyNoMatchProps {
  query: string
  onClear: () => void
}

export function VocabularyNoMatch({ query, onClear }: VocabularyNoMatchProps) {
  const { strings } = useStudioLocale()
  const copy = strings.designSystem.vocabularyPanel
  return (
    <div className="flex flex-col items-center gap-2 p-6 text-center">
      <p className="text-sm text-muted-foreground">{copy.noMatch.replace('{query}', query)}</p>
      <Button variant="ghost" size="sm" onClick={onClear}>
        <X className="mr-1 h-3 w-3" />
        {copy.clear}
      </Button>
    </div>
  )
}
